/**
 * Identity Client
 * 
 * Shared identity client configuration for authentication flows.
 * Used by both admin-console and agent-ui.
 */

import { FrontendApi, Configuration } from '@ory/client';

/**
 * Browser self-service flows in Kratos legitimately use HTTP 422
 * for intermediate states and redirect handoffs.
 */
export const oryBrowserFlowOptions = {
  validateStatus: (status: number) =>
    (status >= 200 && status < 300) || status === 422,
};

type RuntimeIdentityConfig = {
  AUTH_URL?: string;
  AUTH_BROWSER_URL?: string;
  IDENTITY_PUBLIC_URL?: string;
};

function deriveIdentityPublicUrl(): string {
  if (typeof window === 'undefined') {
    return 'https://auth-eu.local.iovalue.tech';
  }

  const { protocol, hostname } = window.location;

  if (hostname.startsWith('auth-')) {
    return `${protocol}//${hostname}`;
  }

  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const regionCode = parts[0];
    const accountRegion = regionCode.split('-')[0] || 'eu';
    const domain = parts.slice(1).join('.');
    return `${protocol}//auth-${accountRegion}.${domain}`;
  }

  return 'https://auth-eu.local.iovalue.tech';
}

/**
 * Get runtime identity provider URLs
 * 
 * These are typically set at runtime via window.__ATLAS_CONFIG__
 * or via Vite env vars during development.
 */
function getIdentityUrls(): { apiUrl: string; browserUrl: string } {
  // Check for runtime config first (set by docker-entrypoint.sh)
  const runtimeConfig = typeof window !== 'undefined'
    ? (window as { __ATLAS_CONFIG__?: RuntimeIdentityConfig }).__ATLAS_CONFIG__
    : undefined;
  const derivedIdentityUrl = deriveIdentityPublicUrl();
  
  // API URL: same-origin proxy for API calls (avoids cross-origin cookie issues)
  // Check AUTH_URL first, then IDENTITY_PUBLIC_URL for app-level compatibility
  const apiUrl = runtimeConfig?.AUTH_URL || 
    runtimeConfig?.IDENTITY_PUBLIC_URL ||
    (typeof import.meta !== 'undefined' ? (import.meta as { env?: { VITE_IDENTITY_URL?: string } }).env?.VITE_IDENTITY_URL : undefined) || 
    derivedIdentityUrl;
  
  // Browser URL: full URL for browser redirects (login page)
  const browserUrl = runtimeConfig?.AUTH_BROWSER_URL || 
    runtimeConfig?.IDENTITY_PUBLIC_URL ||
    (typeof import.meta !== 'undefined' ? (import.meta as { env?: { VITE_IDENTITY_BROWSER_URL?: string } }).env?.VITE_IDENTITY_BROWSER_URL : undefined) || 
    derivedIdentityUrl;
  
  return { apiUrl, browserUrl };
}

/**
 * Create identity client instance
 * 
 * @param basePath - Optional custom base path (defaults to runtime config)
 */
export function createIdentityClient(basePath?: string): FrontendApi {
  const { apiUrl } = getIdentityUrls();
  
  return new FrontendApi(
    new Configuration({
      basePath: basePath || apiUrl,
      baseOptions: {
        withCredentials: true, // Send cookies
      },
    })
  );
}

// Default client instance using runtime configuration
let _identityClient: FrontendApi | null = null;

/**
 * Get the default identity client instance (singleton)
 */
export function getIdentityClient(): FrontendApi {
  if (!_identityClient) {
    _identityClient = createIdentityClient();
  }
  return _identityClient;
}

/**
 * Get identity browser URL for redirects
 */
export function getIdentityBrowserUrl(): string {
  return getIdentityUrls().browserUrl;
}

/**
 * Get identity API URL for API calls
 */
export function getIdentityApiUrl(): string {
  return getIdentityUrls().apiUrl;
}

// Re-export Ory types for convenience
export type { 
  FrontendApi, 
  Session, 
  Identity, 
  LoginFlow, 
  SettingsFlow,
  UiNode,
  UiNodeInputAttributes,
  UiNodeTextAttributes,
  UiNodeImageAttributes,
  UpdateLoginFlowBody,
  UpdateSettingsFlowBody,
} from '@ory/client';
